import { useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { InconsistencyReportListItem } from './InconsistencyReportListItem';
import { SubToolbar } from '../components/TableToolbar/SubToolbar';
import { TableWrapper } from '../components/TableWrapper';
import { useGetInconsistencyReports } from '@/api/queries/useGetInconsistencyReports';
import { InconsistencyReport } from '@/api/types/backend-dto-types';
import EmptyStateCreation from '@/components/molecules/EmptyStateCreation';
import SearchBox from '@/components/molecules/SearchBox';
import Pagination from '@/components/organisms/Pagination';
import { useGeneralPagination } from '@/hooks/useGeneralPagination';
import DataViewToolbar from '@/screens/Chronos/CaseEditor/DataView/components/TableToolbar';

interface Props {
  caseId: string | null;
}

export const InconsistencyReportsList = ({ caseId }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialPageSize = 20;
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const { pagination, setPagination } = useGeneralPagination({ pageSize: initialPageSize });

  const {
    data: responseReports,
    isFetching: isLoadingReports,
    refetch: refetchReports,
  } = useGetInconsistencyReports({
    caseId: caseId ?? '',
    searchQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });
  const noOfPages = responseReports?.pageCount ?? 0;

  const onSearchCall = () => {
    refetchReports();
  };

  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: initialPageSize };
    setPagination(newPaginationState);
    searchParams.set('page', String(newPaginationState.pageIndex + 1));
    navigate(location.pathname + '?' + searchParams.toString());
  };

  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);
  const canGetPrevPage = pagination.pageIndex > 0;
  const canGetNextPage = pagination.pageIndex < (responseReports?.totalCount ?? 0) / initialPageSize - 1;

  return (
    <div className="flex h-full min-w-full flex-col">
      <DataViewToolbar
        titleSection={<h1 className="text-lg font-semibold"> Reports</h1>}
        inlineSearchBar={
          <SearchBox
            value={searchQuery}
            placeholder="Search reports"
            onChange={setSearchQuery}
            onSearchCall={onSearchCall}
          />
        }
        pagination={
          <Pagination
            canGetPrevPage={canGetPrevPage}
            canGetNextPage={canGetNextPage}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={pagination.pageIndex}
            noOfPages={noOfPages}
            isLoadingFacts={isLoadingReports}
            goToPage={goToPage}
          />
        }
      />
      <SubToolbar
        entityType="inconsistency reports"
        totalCount={responseReports?.totalCount}
        totalUnfilteredCount={responseReports?.totalUnfilteredCount}
      />
      <TableWrapper className="flex">
        <div className="h-full w-full overflow-y-auto pr-2">
          {isLoadingReports ? (
            <div className="flex h-full w-full items-center justify-center">
              <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
            </div>
          ) : responseReports?.data && responseReports.data.length ? (
            <div className="flex h-full flex-col gap-2 pt-1 pb-6">
              {responseReports.data.map((report: InconsistencyReport) => {
                return <InconsistencyReportListItem report={report} key={report.id} />;
              })}
            </div>
          ) : (
            <EmptyStateCreation
              title="No inconsistency reports found"
              subtitle="To make your first report, select the inconsistencies you want to include on the inconsistencies table."
              onClick={() => {
                navigate(`/app/chronos/case-editor/data/inconsistencies?caseId=${caseId}`);
              }}
            />
          )}
        </div>
      </TableWrapper>
    </div>
  );
};
